import { appointmentLinkFields } from './appointmentLink.js';
import { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { supabase } from "../../supabaseClient.js";
import useAlert from '../../alert/useAlert.js';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { secureHash, secureUnhash } from '../../utils/patientIdCipher.js';

export const useAppointmentData = () => {
    const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
    const role = useSelector((state) => state.globalState.role, shallowEqual);
    const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
    const allOrgs = useSelector((state) => state.globalState.allOrgs, shallowEqual);
    const { setAlert } = useAlert();
    
    const [loading, setLoading] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [doctors, setDoctors] = useState([]);
    // Keep doctor loading separate from the page-wide loading state. The New Appointment
    // button must stay disabled until this specific lookup has completed.
    const [doctorsLoading, setDoctorsLoading] = useState(true);
    const [doctorsLoadError, setDoctorsLoadError] = useState(null);
    const [selectedDoctorId, setSelectedDoctorId] = useState(null);
    const [showAppointmentForm, setShowAppointmentForm] = useState(false);
    const [isEditingAppointment, setIsEditingAppointment] = useState(false);
    const [currentAppointment, setCurrentAppointment] = useState(null);
    const [isEditingDoctors, setIsEditingDoctors] = useState(false);
    const [view, setView] = useState('month');
    const [filteredCategories, setFilteredCategories] = useState({
        'First consultation': true,
        'Existing patient': true,
        // 'Procedure': true,
        // 'Nurse consultation': true
    });
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);

    // Rules-related state
    const [showRulesView, setShowRulesView] = useState(false);
    const [rules, setRules] = useState([]);
    const [selectedRuleId, setSelectedRuleId] = useState(null);
    const [isEditingRules, setIsEditingRules] = useState(false);
    const [editedRules, setEditedRules] = useState([]);
    const [selectedDoctorsForRule, setSelectedDoctorsForRule] = useState("all");
    const [ruleInstructions, setRuleInstructions] = useState("");

    const [teamMembersData, setTeamMembersData] = useState([]);
    const [showAddDoctorDropdown, setShowAddDoctorDropdown] = useState(false);
    const [addDoctorAnchorEl, setAddDoctorAnchorEl] = useState(null);
    const [subscriptionActiveStatus, setSubscriptionActiveStatus] = useState(null);

    // The team's own clients, for the New Appointment dialog's client picker.
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);

    const escapeHtml = (text = '') =>
        text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

    const convertSummaryToHtml = (summary = '') => {
        if (!summary) return '';
        const lines = summary.split('\n').map((line) => line.trimEnd());
        const htmlChunks = [];
        let insideList = false;

        const closeList = () => {
            if (insideList) {
                htmlChunks.push('</ul>');
                insideList = false;
            }
        };

        lines.forEach((line) => {
            const trimmed = line.trim();
            if (!trimmed) {
                closeList();
                htmlChunks.push('<br/>');
                return;
            }

            if (/^[•\-]/.test(trimmed)) {
                if (!insideList) {
                    htmlChunks.push('<ul>');
                    insideList = true;
                }
                const itemText = escapeHtml(trimmed.replace(/^[•\-\u2022]+\s*/, ''));
                htmlChunks.push(`<li>${itemText}</li>`);
            } else {
                closeList();
                htmlChunks.push(`<p>${escapeHtml(trimmed)}</p>`);
            }
        });

        closeList();
        return htmlChunks.join('');
    };

    const formatDateWithOrdinal = (dateValue) => {
        const parseDate = (value) => {
            if (!value) return new Date();
            const parsed = value instanceof Date ? value : new Date(value);
            return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
        };

        const getOrdinalSuffix = (day) => {
            if (day % 100 >= 11 && day % 100 <= 13) return 'th';
            switch (day % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        const date = parseDate(dateValue);
        const month = date.toLocaleString('en-US', { month: 'short' });
        const day = date.getDate();
        const suffix = getOrdinalSuffix(day);
        const year = date.getFullYear();
        return `${month} ${day}${suffix}, ${year}`;
    };

    // const sanitizePhoneNumber = (value = '') => {
    //     if (!value) return '';
    //     const digits = value.replace(/\D+/g, '').trim();
    //     if (!digits) return '';
    //     if (digits.length > 8 && digits.startsWith('852')) {
    //         return digits.slice(3);
    //     }
    //     return digits;
    // };

    // const appendDatedNote = (existingValue = '', newValue = '', formattedDate) => {
    //     if (!newValue) return existingValue ?? '';
    //     const entry = `${formattedDate} - \n${newValue.trim()}\n\n`;
    //     if (!existingValue) return entry;
    //     const trimmedExisting = (existingValue || '').trimStart();
    //     return `${entry}${trimmedExisting}`;
    // };

    const decryptFieldIfNeeded = async (value) => {
        if (!value) return '';
        try {
            const { data, error } = await supabase.rpc('decrypt_text', { encrypted_data: value });
            if (!error && data) return data;
        } catch (err) {
            console.error('Error decrypting field value:', err);
        }
        return value || '';
    };


    // The client list behind the appointment dialog's picker.
    //
    // Same read and same decryption path the rest of Scribe uses for this table
    // (MedicalScribeData.fetchPatientData / DashboardData): patients whose `team_ids` array
    // contains the current team, with the PII columns run back through the decrypt_text RPC.
    // Only the columns the dialog actually fills are selected and decrypted — the picker
    // needs a name, an email to tell two clients apart, and the phone that
    // appointments.patient_id is derived from (see ClientPicker.js for why).
    const fetchClients = async () => {
        if (!currentOrg?.id) {
            setClients([]);
            return [];
        }
        setClientsLoading(true);
        try {
            // ONE call, not one per field per patient. Decrypting in the browser meant ~6
            // decrypt_text round trips per client; with a dozen clients enough of them were
            // dropped that the dropdown rendered raw ciphertext next to a real name. The RPC
            // decrypts server-side behind the same scribe_row_access rule as every other
            // patient read (step9_scribe_patient_directory.sql).
            const { data, error } = await supabase.rpc('get_scribe_patient_directory', {
                p_team_id: currentOrg.id,
            });

            if (error) {
                console.error('Error fetching clients for the appointment picker:', error);
                setClients([]);
                return [];
            }

            // Belt and braces: if a value ever arrives still encrypted, drop it rather than
            // show it. A blank phone tells the therapist something is missing; \xc30d0409…
            // tells them the product is broken, and they cannot tell which client it belongs to.
            const readable = (value) => {
                const text = typeof value === 'string' ? value.trim() : '';
                return text.startsWith('\\x') ? '' : text;
            };

            const list = (data ?? []).map((client) => ({
                id: client.id,
                name: readable(client.name),
                email: readable(client.email),
                phone_code: readable(client.phone_code),
                phoneNumber: readable(client.phone_number),
                gender: readable(client.gender),
                age: readable(client.age),
            }));

            setClients(list);
            return list;
        } catch (err) {
            console.error('Error fetching clients for the appointment picker:', err);
            setClients([]);
            return [];
        } finally {
            setClientsLoading(false);
        }
    };

    const findMindvaultAccountMatch = async (countryCode, phoneNumber) => {
        const normalizedTarget = `${countryCode}${phoneNumber}`;
        try {
            const { data, error } = await supabase
                .from('mindvault_accounts')
                .select('phone_code, phone_number');

            if (error) throw error;

            for (const entry of data ?? []) {
                const decryptedNumber = await decryptFieldIfNeeded(entry.phone_number);
                const decryptedCode = await decryptFieldIfNeeded(entry.phone_code);
                const normalizedEntry = `${decryptedCode}${decryptedNumber}`;
                if (normalizedEntry && normalizedEntry === normalizedTarget) {
                    return true;
                }
            }
        } catch (lookupError) {
            console.error('Error checking mindvault account records:', lookupError);
        }
        return false;
    };

    const createPatientFromAppointment = async (appointment, teamId) => {
        if (!appointment) {
            setAlert('No appointment selected.', 'error');
            return { success: false };
        }
        if (!teamId) {
            setAlert('Please select a team before creating a patient.', 'error');
            return { success: false };
        }

        // const formattedNoteDate = formatDateWithOrdinal(
        //     appointment.appointment_date || appointment.start || new Date()
        // );
        // const rawPhoneInput = (appointment.patient_id || '').trim();
        // const normalizedPhone = sanitizePhoneNumber(rawPhoneInput);
        const phoneNumber = appointment.patient_id || '';
        if (!phoneNumber) {
            setAlert('Patient ID/phone number is missing for this appointment.', 'error');
            return { success: false };
        }

        const mobileProfileExists = await findMindvaultAccountMatch(appointment.phone_code || '', appointment.phone_number || '');

        if (!mobileProfileExists) {
            setAlert(
                'No patient profile matches this phone number. Please confirm the patient has registered or if the patient has changed their number.',
                'warning'
            );
            return { success: false };
        }

        try {
            const doctorUserId =
                appointment.doctorUserId ??
                doctors.find(
                    (doctor) =>
                        doctor.id === (appointment.primaryDoctorId ?? appointment.doctor_id)
                )?.user_id ??
                null;

            // Look up the patient across ALL teams by full phone (code+number).
            // The RPC (SECURITY DEFINER) does the cross-team match and attaches
            // this team/case-owner to the existing record server-side; it
            // returns 0 rows when no match, in which case we insert below.
            const { data: linkedPatientData, error: linkError } = await supabase.rpc(
                'find_or_link_patient_by_phone',
                {
                    user_phone_full: phoneNumber,
                    p_team_id: teamId,
                    p_case_owner_id: doctorUserId
                }
            );

            if (linkError) throw linkError;

            const existingPatient =
                Array.isArray(linkedPatientData) && linkedPatientData.length > 0
                    ? linkedPatientData[0]
                    : null;

            const ageNumber = parseInt(appointment.age, 10);
            const birthDate =
                !Number.isNaN(ageNumber) && ageNumber > 0
                    ? `${new Date().getFullYear() - ageNumber}-01-01`
                    : null;

            let patientIdToUse = null;
            let patientOperation = 'created';

            if (existingPatient) {
                // A patient with this phone number already exists — the RPC
                // has already attached this team and doctor to the record.
                patientIdToUse = existingPatient.id;
                patientOperation = 'updated';
            } else {
                // const presentingConcernsEntry = appendDatedNote(
                //     '',
                //     appointment.chief_complaint,
                //     formattedNoteDate
                // );
                // const remarksEntry = appendDatedNote('', appointment.remarks, formattedNoteDate);

                const { data: patientInsertData, error: insertError } = await supabase
                    .from('patients')
                    .insert([{
                        name: appointment.patient_name ?? '',
                        firstName: appointment.patient_name ?? '',
                        lastName: '',
                        phone_code: appointment.phone_code ?? '',
                        phoneNumber: appointment.phone_number ?? '',
                        teamid: teamId,
                        team_ids: [teamId],
                        age: appointment.age ?? null,
                        birth_date: birthDate,
                        gender:
                            appointment.gender === 'Male' || appointment.gender === '男'
                                ? 'Male'
                                : 'Female',
                        case_owner: doctorUserId ? [doctorUserId] : [],
                        // presenting_concerns: presentingConcernsEntry,
                        // remarks: remarksEntry
                    }])
                    .select();

                if (insertError) throw insertError;

                const newPatient = patientInsertData?.[0];
                patientIdToUse = newPatient?.id;

                if (!patientIdToUse) {
                    throw new Error('Patient created but ID could not be retrieved.');
                }
            }

            let transcription = '';
            try {
                const { data: messagesData, error: messagesError } = await supabase.rpc('get_textmessages_by_phone', {
                    p_phone: phoneNumber
                });

                if (messagesError) throw messagesError;

                if (Array.isArray(messagesData) && messagesData.length > 0) {
                    const sortedMessages = [...messagesData].sort((a, b) => {
                        const aDate = a?.created_at ? new Date(a.created_at) : null;
                        const bDate = b?.created_at ? new Date(b.created_at) : null;
                        if (aDate && bDate) return aDate.getTime() - bDate.getTime();
                        return 0;
                    });

                    transcription = sortedMessages.reduce((acc, msg) => {
                        const speakerName =
                            msg.senderid === phoneNumber
                                ? appointment.patient_name || 'Patient'
                                : 'AI Assistant';
                        const messageText = msg.message || '';
                        return `${acc}${speakerName} - \n${messageText}\n\n`;
                    }, '');
                }
            } catch (messagesErr) {
                console.error('Error assembling transcription from text messages:', messagesErr);
            }

            const consultationId = uuidv4();

            try {
                const { data: consultationInsertData, error: consultationError } = await supabase
                    .from('consultations')
                    .insert([{
                        id: consultationId,
                        patient_id: patientIdToUse,
                        transcription,
                        notes: null,
                        chiefComplaint: appointment.chief_complaint ?? ''
                    }])
                    .select();

                if (consultationError) throw consultationError;

                const insertedConsultationId =
                    consultationInsertData?.[0]?.id ?? consultationId;

                const formattedDate = new Date().toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                });

                const formattedSummaryHtml = convertSummaryToHtml(appointment.ai_summary);

                const { error: reportError } = await supabase.from('generatedreports').insert({
                    patientId: patientIdToUse,
                    consultationId: insertedConsultationId,
                    content: formattedSummaryHtml,
                    title: `Chat Summary Report – ${formattedDate}`,
                    created_by: userDetails?.name || 'Unknown User',
                    locked: false,
                    documents: []
                });

                if (reportError) throw reportError;

                setAlert(
                    patientOperation === 'created'
                        ? 'Patient created successfully!'
                        : 'Patient updated successfully!',
                    'success'
                );
                return { success: true };
            } catch (postCreationError) {
                console.error('Error creating consultation/report:', postCreationError);
                setAlert(
                    patientOperation === 'created'
                        ? 'Patient created, but consultation/report creation failed.'
                        : 'Patient updated, but consultation/report creation failed.',
                    'warning'
                );
                return { success: true, partial: true };
            }
        } catch (error) {
            console.error('Error creating patient from appointment:', error);
            setAlert('Failed to create patient.', 'error');
            return { success: false, error };
        }
    };

    const fetchSubscriptionInfo = async () => {
        try {
        if (!userDetails || !userDetails.id) return;
        
        const { data, error } = await supabase.from('subscription_info').select('is_active').eq('user_id', userDetails.id).single();
            
        if (error && error.code !== 'PGRST116') {
            console.error('Error fetching subscription info:', error);
            return;
        }
        if(!data) {
            setSubscriptionActiveStatus(false);
            return;
        }
        if (data) {
            setSubscriptionActiveStatus(data.is_active);
        }
        } catch (error) {
        console.error('Error in fetchSubscriptionInfo:', error);
        }
    };

    const fetchDoctors = async () => {
    if (!currentOrg?.id) {
        setDoctors([]);
        setSelectedDoctorId(null);
        setDoctorsLoading(false);
        setDoctorsLoadError(null);
        return [];
    }

    try {
        setLoading(true);
        setDoctorsLoading(true);
        setDoctorsLoadError(null);

        const { data: doctorsData, error: doctorsError } = await supabase
        .from('doctors')
        .select('*')
        .eq('team_id', currentOrg.id)
        .order('display_order', { ascending: true });

        if (doctorsError) throw doctorsError;

        if (!doctorsData || doctorsData.length === 0) {
            setDoctors([]);
            setSelectedDoctorId(null);
            return [];
        }

        const isAdminUser = role?.toLowerCase() === 'admin';

        const userIds = doctorsData
            .filter((doctor) => doctor.user_id)
            .map((doctor) => doctor.user_id);

        if (userIds.length === 0) {
            const markedDoctors = doctorsData.map((doctor) => ({
                ...doctor,
                isAccessible: isAdminUser || doctor.user_id === userDetails?.id,
            }));
            const ownDoctor = markedDoctors.find((d) => d.user_id === userDetails?.id);
            setDoctors(markedDoctors);
            setSelectedDoctorId((prev) => prev ?? (ownDoctor?.id || markedDoctors[0].id));
            return markedDoctors;
        }

        const { data: usersData, error: usersError } = await supabase
        .from('user_accounts')
        .select('*')
        .in('id', userIds);

        if (usersError) {
            console.error('Error fetching user accounts:', usersError);
            const markedDoctors = doctorsData.map((doctor) => ({
                ...doctor,
                isAccessible: isAdminUser || doctor.user_id === userDetails?.id,
            }));
            const ownDoctor = markedDoctors.find((d) => d.user_id === userDetails?.id);
            setDoctors(markedDoctors);
            setSelectedDoctorId((prev) => prev ?? (ownDoctor?.id || markedDoctors[0].id));
            return markedDoctors;
        }

        const decryptedUsers = await Promise.all(
        usersData.map(async user => {
            try {
            const [name, email, phone_number] = await Promise.all([
                user.name
                ? supabase.rpc('decrypt_text', { encrypted_data: user.name })
                : { data: '', error: null },
                user.email
                ? supabase.rpc('decrypt_text', { encrypted_data: user.email })
                : { data: '', error: null },
                user.phone_number
                ? supabase.rpc('decrypt_text', { encrypted_data: user.phone_number })
                : { data: '', error: null }
            ]);

            if (name.error || email.error || phone_number.error) {
                console.error(`Decryption error for user ${user.id}:`, {
                name: name.error,
                email: email.error,
                phone_number: phone_number.error
                });

                return {
                ...user,
                name: user.name || '',
                email: user.email || '',
                phone_number: user.phone_number || ''
                };
            }

            return {
                ...user,
                name: name.data || '',
                email: email.data || '',
                phone_number: phone_number.data || ''
            };
            } catch (error) {
            console.error(`Error decrypting user ${user.id}:`, error);
            return user;
            }
        })
        );

        const decryptedUsersMap = {};
        decryptedUsers.forEach(user => {
        decryptedUsersMap[user.id] = user;
        });

        const doctorsWithUserDetails = doctorsData.map((doctor) => {
            const decryptedUser = decryptedUsersMap[doctor.user_id];

            if (decryptedUser) {
                return {
                ...doctor,
                name: decryptedUser.name,
                email: decryptedUser.email,
                phone_number: decryptedUser.phone_number,
                isAccessible: isAdminUser || doctor.user_id === userDetails?.id,
                };
            }

            return {
                ...doctor,
                name: doctor.name || 'Unknown Doctor',
                email: doctor.email || '',
                phone_number: doctor.phone_number || '',
                isAccessible: isAdminUser || doctor.user_id === userDetails?.id,
            };
        });

        setDoctors(doctorsWithUserDetails);
        if (doctorsWithUserDetails.length > 0) {
            const ownDoctor = doctorsWithUserDetails.find((d) => d.user_id === userDetails?.id);
            setSelectedDoctorId((prev) => prev ?? (ownDoctor?.id || doctorsWithUserDetails[0].id));
        } else {
            setSelectedDoctorId(null);
        }
        return doctorsWithUserDetails;
    } catch (error) {
        console.error('Error fetching doctors:', error);
        setDoctorsLoadError(error);
        setAlert('Failed to fetch doctors.', 'error');
        return [];
    } finally {
        setLoading(false);
        setDoctorsLoading(false);
    }
    };

    const fetchAppointments = async (doctorListParam = doctors) => {
    try {
        setLoading(true);

        const baseDoctors = Array.isArray(doctorListParam) ? doctorListParam : doctors;
        const persistedBaseDoctors = baseDoctors.filter((doctor) => {
        if (!doctor || !doctor.id) return false;
        return !(typeof doctor.id === 'string' && doctor.id.startsWith('temp-'));
        });

        if (persistedBaseDoctors.length === 0) {
        setAppointments([]);
        return [];
        }

        const baseDoctorUserIds = [
        ...new Set(persistedBaseDoctors.map((doctor) => doctor.user_id).filter(Boolean))
        ];

        let relatedDoctors = [];
        if (baseDoctorUserIds.length > 0) {
        const { data: relatedDoctorsData, error: relatedDoctorsError } = await supabase
            .from('doctors')
            .select('id, user_id, team_id')
            .in('user_id', baseDoctorUserIds);

        if (relatedDoctorsError) throw relatedDoctorsError;
        relatedDoctors = relatedDoctorsData ?? [];
        }

        const doctorIdToUserId = new Map();
        const userIdToPrimaryDoctorId = new Map();
        const currentTeamDoctorIds = new Set();

        persistedBaseDoctors.forEach((doctor) => {
        doctorIdToUserId.set(doctor.id, doctor.user_id || null);
        currentTeamDoctorIds.add(doctor.id);

        if (doctor.user_id && !userIdToPrimaryDoctorId.has(doctor.user_id)) {
            userIdToPrimaryDoctorId.set(doctor.user_id, doctor.id);
        }
        });

        relatedDoctors.forEach((doctor) => {
            if (!doctor || !doctor.id) return;
            doctorIdToUserId.set(doctor.id, doctor.user_id || null);
        });

        const doctorIdsToQuery = [...doctorIdToUserId.keys()];
        if (doctorIdsToQuery.length === 0) {
            setAppointments([]);
            return [];
        }

        const { data, error } = await supabase
            .from('appointments')
            .select('*')
            .in('doctor_id', doctorIdsToQuery);

        if (error) throw error;

        // Decrypt all appointments
        const decryptedAppointments = await Promise.all(
            data.map(async (appointment) => {
                try {
                    const [
                        patientName,
                        phoneCode,
                        phoneNumber,
                        gender,
                        age,
                        patientId,
                        chiefComplaint,
                        category,
                        recurring,
                        remarks,
                        aiSummary,
                        dob
                    ] = await Promise.all([
                        appointment.patient_name ? supabase.rpc('decrypt_text', { encrypted_data: appointment.patient_name }) : { data: '', error: null },
                        appointment.phone_code ? supabase.rpc('decrypt_text', { encrypted_data: appointment.phone_code }) : { data: '', error: null },
                        appointment.phone_number ? supabase.rpc('decrypt_text', { encrypted_data: appointment.phone_number }) : { data: '', error: null },
                        appointment.gender ? supabase.rpc('decrypt_text', { encrypted_data: appointment.gender }) : { data: '', error: null },
                        appointment.age ? supabase.rpc('decrypt_text', { encrypted_data: appointment.age }) : { data: '', error: null },
                        appointment.patient_id ? supabase.rpc('decrypt_text', { encrypted_data: appointment.patient_id }) : { data: '', error: null },
                        appointment.chief_complaint ? supabase.rpc('decrypt_text', { encrypted_data: appointment.chief_complaint }) : { data: '', error: null },
                        appointment.category ? supabase.rpc('decrypt_text', { encrypted_data: appointment.category }) : { data: '', error: null },
                        appointment.recurring ? supabase.rpc('decrypt_text', { encrypted_data: appointment.recurring }) : { data: '', error: null },
                        appointment.remarks ? supabase.rpc('decrypt_text', { encrypted_data: appointment.remarks }) : { data: '', error: null },
                        appointment.ai_summary ? supabase.rpc('decrypt_text', { encrypted_data: appointment.ai_summary }) : { data: '', error: null },
                        appointment.dob ? supabase.rpc('decrypt_text', { encrypted_data: appointment.dob }) : { data: '', error: null }
                    ]);

                    const decryptedPatientId = patientId.data || '';
                    let unhashedPatientId = '';

                    if (decryptedPatientId) {
                        try {
                            unhashedPatientId = await secureUnhash(
                                decryptedPatientId,
                                process.env.REACT_APP_PATIENT_ID_SECRET
                            );
                        } catch (e) {
                            unhashedPatientId = decryptedPatientId;
                        }
                    }

                    return {
                        ...appointment,
                        patient_name: patientName.data || '',
                        phone_code: phoneCode.data || '',
                        phone_number: phoneNumber.data || '',
                        gender: gender.data || '',
                        age: age.data || '',
                        patient_id: unhashedPatientId,
                        hashed_patient_id: decryptedPatientId,
                        chief_complaint: chiefComplaint.data || '',
                        category: category.data || '',
                        recurring: recurring.data || 'None',
                        remarks: remarks.data || '',
                        ai_summary: aiSummary.data || '',
                        dob: dob.data || ''
                    };
                } catch (decryptError) {
                    console.error(`Error decrypting appointment ${appointment.id}:`, decryptError);
                    return appointment;
                }
            })
        );

        const allAppointments = [];

        decryptedAppointments.forEach((appointment) => {
            const appointmentDate = new Date(appointment.appointment_date);
            if (Number.isNaN(appointmentDate.getTime())) return;

            const year = appointmentDate.getFullYear();
            const month = appointmentDate.getMonth();
            const day = appointmentDate.getDate();

            const startDate = new Date();
            startDate.setFullYear(year, month, day);
            if (appointment.appointment_starttime) {
                const startTime = new Date(appointment.appointment_starttime);
                startDate.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
            } else {
                startDate.setHours(9, 0, 0, 0);
            }

            const endDate = new Date();
            endDate.setFullYear(year, month, day);
            if (appointment.appointment_endtime) {
                const endTime = new Date(appointment.appointment_endtime);
                endDate.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
            } else {
                endDate.setHours(startDate.getHours() + 1, startDate.getMinutes(), 0, 0);
            }

            const userId = doctorIdToUserId.get(appointment.doctor_id);
            const primaryDoctorId = userId && userIdToPrimaryDoctorId.has(userId)
                ? userIdToPrimaryDoctorId.get(userId)
                : appointment.doctor_id;

            const mappedAppointment = {
                ...appointment,
                start: startDate,
                end: endDate,
                originalDoctorId: appointment.doctor_id,
                primaryDoctorId,
                belongsToCurrentTeamDoctor: String(appointment.team_id) === String(currentOrg?.id),
                doctorUserId: userId ?? null,
            };

            allAppointments.push(mappedAppointment);

            if (appointment.recurring && appointment.recurring !== 'None') {
                const recurrences = generateRecurringAppointments(mappedAppointment);
                allAppointments.push(...recurrences);
            }
        });

        setAppointments(allAppointments);
        return allAppointments;
    } catch (error) {
        console.error('Error fetching appointments:', error);
        setAlert('Failed to fetch appointments.', 'error');
        setAppointments([]);
        return [];
    } finally {
        setLoading(false);
    }
    };

    // Helper function to generate recurring appointments
    const generateRecurringAppointments = (baseAppointment) => {
        const recurringInstances = [];
        const recurringType = baseAppointment.recurring;
        
        // Extract time information to ensure consistent time slots
        const startHours = baseAppointment.start.getHours();
        const startMinutes = baseAppointment.start.getMinutes();
        const endHours = baseAppointment.end.getHours();
        const endMinutes = baseAppointment.end.getMinutes();
        
        // Generate instances for up to 1 year in the future
        const endDateLimit = new Date();
        endDateLimit.setFullYear(endDateLimit.getFullYear() + 1);
        
        // Start from the day after the base appointment
        let currentDate = new Date(baseAppointment.start);
        currentDate.setDate(currentDate.getDate() + 1);
        
        while (currentDate < endDateLimit) {
            // Apply the recurring pattern
            if (recurringType === 'Every day') {
                // No additional date adjustment needed for daily recurrence
            } else if (recurringType === 'Every week') {
                // Jump to next week
                currentDate.setDate(currentDate.getDate() + 6);
            } else if (recurringType === 'Every 2 weeks') {
                // Jump two weeks ahead
                currentDate.setDate(currentDate.getDate() + 13);
            } else if (recurringType === 'Every month') {
                // Move to the next month, same day
                currentDate.setMonth(currentDate.getMonth() + 1);
            }
            
            // Skip if we've gone beyond our limit
            if (currentDate >= endDateLimit) break;
            
            // Extract date components
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const day = currentDate.getDate();
            
            // Create properly normalized dates for this instance
            const instanceStart = new Date();
            instanceStart.setFullYear(year, month, day);
            instanceStart.setHours(startHours, startMinutes, 0, 0);
            
            const instanceEnd = new Date();
            instanceEnd.setFullYear(year, month, day);
            instanceEnd.setHours(endHours, endMinutes, 0, 0);
            
            recurringInstances.push({
                ...baseAppointment,
                id: `${baseAppointment.id}-recur-${instanceStart.getTime()}`,
                start: instanceStart,
                end: instanceEnd,
                appointment_date: instanceStart,
                appointment_starttime: instanceStart,
                appointment_endtime: instanceEnd,
                isRecurringInstance: true,
                originalAppointmentId: baseAppointment.id
            });
            
            // Move to the next day for the next iteration
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        return recurringInstances;
    };


    const fetchRules = async () => {
        try {
            setLoading(true);
            
            // Uncomment when supabase is set up
            const { data, error } = await supabase
                .from('appointment_rules')
                .select('*')
                .eq('team_id', currentOrg.id)
                .order('display_order', { ascending: true });
                
            if (error) throw error;
            setRules(data);
            if (data && data.length > 0) {
                setSelectedRuleId(data[0].id);
                setRuleInstructions(data[0].instructions);
                setSelectedDoctorsForRule(data[0].applicable_doctors);
            }
            
            // Using mock data for now
            // const mockRules = generateMockRules();
            // setRules(mockRules);
            // if (mockRules.length > 0) {
            //     setSelectedRuleId(mockRules[0].id);
            //     setRuleInstructions(mockRules[0].instructions);
            //     setSelectedDoctorsForRule(mockRules[0].applicable_doctors);
            // }
        } catch (error) {
            console.error('Error fetching rules:', error);
            setAlert('Failed to fetch appointment rules.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getTeamMembers = async () => {
    if(!currentOrg) {
        return;
    }
    try {
        const { data: memberData, error: memberDataError } = await supabase
        .from('team_users')
        .select('user_id')
        .eq('team_id', currentOrg ? currentOrg.id : '');
        
        if (memberDataError) {
        console.error("Error fetching team members:", memberDataError);
        return;
        }
        
        const teamMembersIDs = memberData.map(member => member.user_id);
        const { data: usersData, error: usersError } = await supabase
        .from('user_accounts')
        .select('id, name')
        .in('id', teamMembersIDs);
        
        if (usersError) {
        console.error("Error fetching user accounts:", usersError);
        return;
        }
        
        const decryptedUsers = await Promise.all(
        usersData.map(async (userData) => {
            const { data: decryptedName, error: decryptError } = await supabase.rpc(
            'decrypt_text', 
            { encrypted_data: userData.name }
            );
            
            if (decryptError) {
            console.error("Error decrypting name:", decryptError);
            return {
                id: userData.id,
                name: "Error decrypting name"
            };
            }
            return {
            id: userData.id,
            name: decryptedName
            };
        })
        );
        setTeamMembersData(decryptedUsers);
    } catch (error) {
        console.error("Error fetching team members:", error);
    }
    };

    const saveDoctor = async (doctorData) => {
        try {
            const doctorToSave = {
                team_id: doctorData.team_id,
                display_order: doctorData.display_order,
                ...(doctorData.user_id && { user_id: doctorData.user_id })
            };

            if (doctorData.isNew) {
                const { data, error } = await supabase
                    .from('doctors')
                    .insert([doctorToSave])
                    .select();
                    
                if (error) throw error;
                return data[0];
            } 
            else {
                const { data, error } = await supabase
                    .from('doctors')
                    .update(doctorToSave)
                    .eq('id', doctorData.id)
                    .select();
                    
                if (error) throw error;
                return data[0];
            }
        } catch (error) {
            console.error('Error saving doctor:', error);
            setAlert('Error saving doctor', 'error');
            return null;
        }
    };

    const saveRule = async (rule) => {
        try {
            setLoading(true);
            // For a new rule
            if (rule.isNew) {
                const { data, error } = await supabase
                    .from('appointment_rules')
                    .insert({ 
                        title: rule.title, 
                        instructions: rule.instructions,
                        applicable_doctors: rule.applicable_doctors,
                        team_id: currentOrg.id,
                        display_order: rule.display_order
                    })
                    .select();
                    
                if (error) throw error;
                setRules([...rules, data[0]]);
                
                // Mock for now
                // const newRule = {
                //     id: uuidv4(),
                //     title: rule.title,
                //     instructions: rule.instructions,
                //     applicable_doctors: rule.applicable_doctors,
                //     team_id: currentOrg?.id || '1'
                // };
                // setRules([...rules, newRule]);

                setAlert('Rule added successfully!', 'success');
            } else {
                // Update existing rule
                const { error } = await supabase
                    .from('appointment_rules')
                    .update({ 
                        title: rule.title,
                        instructions: rule.instructions,
                        applicable_doctors: rule.applicable_doctors,
                        display_order: rule.display_order
                    })
                    .eq('id', rule.id);
                    
                if (error) throw error;
                
                const updatedRules = rules.map(r => 
                    r.id === rule.id ? { ...r, ...rule } : r
                );
                setRules(updatedRules);
                setAlert('Rule updated successfully!', 'success');
            }
            await fetchRules(); 
        } catch (error) {
            console.error('Error saving rule:', error);
            setAlert('Failed to save rule.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const deleteDoctor = async (doctorId) => {
        try {
            setLoading(true);
            
            // Uncomment when supabase is set up
            const { error } = await supabase
                .from('doctors')
                .delete()
                .eq('id', doctorId);
                
            if (error) throw error;
            
            const updatedDoctors = doctors.filter(d => d.id !== doctorId);
            setDoctors(updatedDoctors);
            
            // If deleted doctor was selected, select the first available doctor
            if (selectedDoctorId === doctorId && updatedDoctors.length > 0) {
                setSelectedDoctorId(updatedDoctors[0].id);
            }
            
            setAlert('Doctor deleted successfully!', 'success');
            return true;
        } catch (error) {
            console.error('Error deleting doctor:', error);
            setAlert('Failed to delete doctor.', 'error');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const deleteRule = async (ruleId) => {
        try {
            setLoading(true);
            
            // Uncomment when supabase is set up
            const { error } = await supabase
                .from('appointment_rules')
                .delete()
                .eq('id', ruleId);
                
            if (error) throw error;
            
            const updatedRules = rules.filter(r => r.id !== ruleId);
            setRules(updatedRules);
            
            // If deleted rule was selected, select the first available rule
            if (selectedRuleId === ruleId && updatedRules.length > 0) {
                setSelectedRuleId(updatedRules[0].id);
                setRuleInstructions(updatedRules[0].instructions);
                setSelectedDoctorsForRule(updatedRules[0].applicable_doctors);
            }
            
            setAlert('Rule deleted successfully!', 'success');
            return true;
        } catch (error) {
            console.error('Error deleting rule:', error);
            setAlert('Failed to delete rule.', 'error');
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Writes a therapist-scheduled appointment through the SECURITY DEFINER RPCs in
    // step8_appointments_therapist_write_rpcs.sql, NOT through .from('appointments').
    // Migration 20260902000001_money_path_write_lockdown left `authenticated` with no INSERT
    // at all and a column-level UPDATE on six columns only, so the direct writes this
    // function used to do answered 42501 on every single call from 2026-09-02 onwards.
    //
    // The RPC signatures carry NO money parameter, deliberately: payment_status,
    // booking_status and every amount column stay at their table defaults
    // ('pending' / 'draft' / NULL), and those defaults are the whole reason a
    // therapist-created row is invisible to the monthly payout, the refund path and the
    // free-connection rule. Never add a payment field to these payloads — a row that can be
    // both therapist-created and paid is a bank transfer for a session nobody paid for.
    //
    // Returns { ok: true, id } or { ok: false, error } and never throws. The caller needs
    // the error object (not a toast) so the form dialog can stay open with the therapist's
    // typed values intact and show the real reason inline.
    const saveAppointment = async (appointment) => {
        let saved = null;
        try {
            setLoading(true);
            if (!appointment.doctor_id) {
                // Shaped like the RPC's own refusal so the caller can run it through the
                // same appointmentErrorMessage() mapping as a real database error.
                return { ok: false, error: { code: '22023', message: 'team and therapist are required' } };
            }

            // Extract date components from appointment_date
            const appointmentDate = new Date(appointment.appointment_date);
            const year = appointmentDate.getFullYear();
            const month = appointmentDate.getMonth();
            const day = appointmentDate.getDate();
            
            // Create properly normalized start time
            const startTime = new Date(appointment.appointment_starttime);
            const normalizedStartTime = new Date();
            normalizedStartTime.setFullYear(year, month, day); // Set proper date from appointment_date
            normalizedStartTime.setHours(
                startTime.getHours(),
                startTime.getMinutes(),
                0, 0
            );
            
            // Create properly normalized end time
            const endTime = new Date(appointment.appointment_endtime);
            const normalizedEndTime = new Date();
            normalizedEndTime.setFullYear(year, month, day); // Set proper date from appointment_date
            normalizedEndTime.setHours(
                endTime.getHours(),
                endTime.getMinutes(),
                0, 0
            );
            
            const appointmentData = {
                ...appointment,
                appointment_starttime: normalizedStartTime,
                appointment_endtime: normalizedEndTime,
                start: normalizedStartTime,
                end: normalizedEndTime,
                // No '1' fallback any more: p_team_id is a uuid the RPC checks against the
                // caller's team_users membership, so a placeholder would only turn a missing
                // org into an unreadable 22P02. null makes the RPC raise its own 22023
                // ("team and therapist are required"), which the caller can explain.
                team_id: currentOrg?.id || null
            };

            // The MUI date/time fields are typeable, so a cleared one arrives as null and a
            // half-typed one as an Invalid Date. Without this the .toISOString() below throws
            // and the therapist reads "Cannot read properties of null (reading 'toISOString')"
            // in the dialog's error banner instead of being told to pick a date.
            const invalidDate = [
                ['date', appointmentData.appointment_date],
                ['start time', normalizedStartTime],
                ['end time', normalizedEndTime],
            ].find(([, value]) => !(value instanceof Date) || Number.isNaN(value.getTime()));
            if (invalidDate) {
                return { ok: false, error: { code: '22007', message: `Pick a valid ${invalidDate[0]} for the appointment.` } };
            }

            const hashedPatientId = appointmentData.patient_id ? await secureHash(appointmentData.patient_id, process.env.REACT_APP_PATIENT_ID_SECRET) : '';
            const doctorUserId = doctors.find((doctor) => doctor.id === appointmentData.doctor_id)?.user_id ?? null;

            // Every non-money column the form owns, in the order the RPCs declare them.
            // Values go over as PLAINTEXT: trig_appointments pgp_sym_encrypts the PII on the
            // way in, so encrypting here would double-encrypt and never decrypt back.
            const rpcFields = {
                p_doctor_id: appointmentData.doctor_id,
                p_patient_name: appointmentData.patient_name || null,
                // Still the SHA-hashed phone number, unchanged: this is what MindVault
                // matches a client account against (useAppointments.ts decrypts it, unhashes
                // it and compares it to the account's phone). Derived, never typed.
                p_patient_id: hashedPatientId || null,
                p_phone_code: appointmentData.phone_code || null,
                p_phone_number: appointmentData.phone_number || null,
                p_gender: appointmentData.gender || null,
                p_age: appointmentData.age === '' || appointmentData.age == null ? null : String(appointmentData.age),
                p_chief_complaint: appointmentData.chief_complaint || null,
                p_category: appointmentData.category || null,
                p_recurring: appointmentData.recurring || null,
                p_session_type: appointmentData.session_type || null,
                p_virtual_link: appointmentData.virtual_link || null,
                p_remarks: appointmentData.remarks || null,
                p_ai_summary: appointmentData.ai_summary || null,
                p_appointment_date: appointmentData.appointment_date.toISOString(),
                p_appointment_starttime: normalizedStartTime.toISOString(),
                p_appointment_endtime: normalizedEndTime.toISOString(),
            };

            if (!appointment.id) {
                // New appointment. RETURNS the new row's uuid — nothing else, so there is no
                // round trip through decrypt_text any more: we already hold the plaintext we
                // just sent, and the refetch below is what reconciles the row with whatever
                // the server defaulted (payment_status='pending', booking_status='draft').
                const { data: newId, error } = await supabase.rpc('create_therapist_appointment_v2', {
                    ...appointmentLinkFields(appointment),
                    p_team_id: appointmentData.team_id,
                    ...rpcFields,
                });

                if (error) return { ok: false, error };

                const newAppointment = {
                    ...appointmentData,
                    id: newId,
                    start: normalizedStartTime,
                    end: normalizedEndTime,
                    doctorUserId,
                };

                setAppointments([...appointments, newAppointment]);

                setAlert('Appointment created successfully!', 'success');
                saved = { ok: true, id: newId };
            } else {
                // Update existing appointment. No p_team_id: the RPC re-reads the row's own
                // team and re-checks p_doctor_id against it, so an edit cannot move the
                // session (and its payout) onto another clinic's therapist.
                const { error } = await supabase.rpc('update_therapist_appointment_v2', {
                    ...appointmentLinkFields(appointment),
                    p_appointment_id: appointment.id,
                    ...rpcFields,
                });

                if (error) return { ok: false, error };

                const appointmentDataWithDoctor = {
                    ...appointmentData,
                    doctorUserId,
                };

                const updatedAppointments = appointments.map(a =>
                    a.id === appointment.id ? appointmentDataWithDoctor : a
                );
                setAppointments(updatedAppointments);
                setAlert('Appointment updated successfully!', 'success');
                saved = { ok: true, id: appointment.id };
            }
        } catch (error) {
            console.error('Error saving appointment:', error);
            return { ok: false, error };
        } finally {
            setLoading(false);
        }

        // Deliberately OUTSIDE the try above. The write has already committed by the time we
        // get here, so a failing refresh means a stale calendar, not a lost appointment —
        // and reporting it as "Failed to save appointment." (which is what the old
        // `await fetchAppointments()` inside the try did) sends the therapist off to create
        // the same session a second time.
        try {
            await fetchAppointments();
        } catch (refreshError) {
            console.error('Appointment saved, but refreshing the calendar failed:', refreshError);
        }

        return saved;
    };

    // Cancelling splits on ONE question: who owes the client their credits back?
    //
    //   payment_status === 'paid'  -> POST /stripe/appointments/:id/cancel-refund. That
    //     endpoint (refund_service.py) writes the credit ledger and refuses if the monthly
    //     payout already transferred the therapist's share. It is the ONLY path that
    //     actually returns the client's credits, so a paid row must never be cancelled any
    //     other way — flipping appointment_status instead would strand the credits with no
    //     record that anything was owed.
    //
    //   anything else -> cancel_therapist_appointment(). A therapist-created row is
    //     payment_status='pending' / booking_status='draft': no money ever moved, so there
    //     is nothing to refund and this path must NEVER post to /cancel-refund. That is
    //     exactly what the old code did unconditionally, and the backend answered 422
    //     "This appointment has no completed payment to refund" — the cancellation never
    //     landed and the therapist was told the cancel failed. The RPC also persists
    //     cancellation_note, which the direct UPDATE this function used to do dropped
    //     silently (it 42501'd under the money-path lockdown and the error was only logged).
    //
    // The RPC refuses a paid row and a non-draft unpaid row itself, so this branch is a
    // routing decision, not the security boundary.
    //
    // Returns { ok: true } or { ok: false, error }, so the caller can keep the cancel dialog
    // open with the typed note and show the real reason instead of a generic failure.
    const deleteAppointment = async (appointmentId, cancellationNote = '') => {
        try {
            setLoading(true);

            // payment_status comes from the loaded row (fetchAppointments selects '*'). When
            // the row is not in memory — an optimistic row straight after create, or a
            // recurring parent filtered out of the view — ask the table: SELECT is still
            // granted to `authenticated`, and guessing wrong here either strands a client's
            // credits or throws an unpaid row at the refund endpoint.
            let paymentStatus = appointments.find((a) => a.id === appointmentId)?.payment_status;
            if (paymentStatus === undefined) {
                const { data: row, error: lookupError } = await supabase
                    .from('appointments')
                    .select('payment_status')
                    .eq('id', appointmentId)
                    .maybeSingle();

                if (lookupError) return { ok: false, error: lookupError };
                paymentStatus = row?.payment_status ?? null;
            }

            if (paymentStatus !== 'paid') {
                const { error } = await supabase.rpc('cancel_therapist_appointment', {
                    p_appointment_id: appointmentId,
                    p_note: cancellationNote || null,
                });

                if (error) return { ok: false, error };

                setAlert('Appointment cancelled.', 'success');
                return { ok: true };
            }

            const backendUrl = process.env.REACT_APP_STRIPE_CONNECT_URL;
            if (!backendUrl) {
                throw new Error("Backend Stripe Connect URL is not configured.");
            }

            // The cancel-refund endpoint requires a verified Supabase JWT
            // (backend get_authenticated_user_id → 401 "Authentication required"
            // without it). Attach the current session's access token.
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData?.session?.access_token;

            const response = await fetch(
                `${backendUrl}/stripe/appointments/${appointmentId}/cancel-refund`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
                    },
                    body: JSON.stringify({ cancellation_note: cancellationNote })
                }
            );

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                // Carry the HTTP status on the error: appointmentErrorMessage falls back to
                // error.status when there is no SQLSTATE, so without this a backend failure
                // whose message we do not recognise reaches the therapist with no code at all
                // and the bug report has nothing to chase.
                const backendError = new Error(data.error || 'Failed to cancel the appointment on the backend.');
                backendError.status = response.status;
                throw backendError;
            }

            setAlert('Appointment cancelled and patient refunded successfully!', 'success');
            return { ok: true };
        } catch (error) {
            console.error('Error cancelling appointment:', error);
            return { ok: false, error };
        } finally {
            setLoading(false);
        }
    };

    // Handle rule selection change
    const handleRuleChange = (ruleId) => {
        const selectedRule = rules.find(rule => rule.id === ruleId);
        if (selectedRule) {
            setSelectedRuleId(ruleId);
            setRuleInstructions(selectedRule.instructions);
            setSelectedDoctorsForRule(selectedRule.applicable_doctors);
        }
    };

    // Save rule instructions
    const saveRuleInstructions = async () => {
        if (rules.length === 0) {
            setAlert('Please create a rule first before saving instructions.', 'error');
            return;
        }
        if (!selectedRuleId) {
            setAlert('Please select a rule before saving instructions.', 'error');
            return;
        }
        const selectedRule = rules.find(rule => rule.id === selectedRuleId);
        if (selectedRule) {
            await saveRule({
                ...selectedRule,
                instructions: ruleInstructions,
                applicable_doctors: selectedDoctorsForRule,
            });
        }
    };

    // Initial data fetch
    useEffect(() => {
    if (currentOrg?.id) {
        const init = async () => {
        await fetchSubscriptionInfo();
        const loadedDoctors = await fetchDoctors();
        await fetchAppointments(loadedDoctors);
        await fetchRules();
        await getTeamMembers();
        await fetchClients();
        };

        init();
    } else {
        setDoctors([]);
        setSelectedDoctorId(null);
        setDoctorsLoading(false);
        // The picker's list is per-team too: leaving the previous team's clients on screen
        // would offer a therapist people they can no longer book.
        setClients([]);
    }
    }, [currentOrg?.id]);

    return { 
        loading,
        appointments,
        doctors,
        doctorsLoading,
        doctorsLoadError,
        selectedDoctorId,
        setSelectedDoctorId,
        showAppointmentForm,
        setShowAppointmentForm,
        isEditingAppointment,
        setIsEditingAppointment,
        currentAppointment,
        setCurrentAppointment,
        isEditingDoctors,
        setIsEditingDoctors,
        view,
        setView,
        filteredCategories,
        setFilteredCategories,
        selectedDate,
        setSelectedDate,
        showAppointmentDetails,
        setShowAppointmentDetails,
        selectedAppointment,
        setSelectedAppointment,
        fetchDoctors,
        fetchAppointments,
        saveDoctor,
        deleteDoctor,
        saveAppointment,
        deleteAppointment,
        showRulesView,
        setShowRulesView,
        rules,
        selectedRuleId,
        setSelectedRuleId,
        isEditingRules,
        setIsEditingRules,
        editedRules,
        setEditedRules,
        selectedDoctorsForRule,
        setSelectedDoctorsForRule,
        ruleInstructions,
        setRuleInstructions,
        fetchRules,
        saveRule,
        deleteRule,
        handleRuleChange,
        saveRuleInstructions,
        currentOrg,

        setAppointments,
        setAlert,
        userDetails,
        teamMembersData,
        showAddDoctorDropdown,
        setShowAddDoctorDropdown,
        addDoctorAnchorEl,
        setAddDoctorAnchorEl,
        subscriptionActiveStatus,

        role,
        allOrgs,
        createPatientFromAppointment,
        clients,
        clientsLoading,
        fetchClients
    };
};

